/**
 * Theme toggle hook (store + persist dark mode to preferences).
 */

import { useThemeStore } from '@/stores/themeStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useMutation } from '@tanstack/react-query';
import { updateUserPreferences } from '@/services/preferences.service';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Dark-mode toggle that writes both the theme store and `PUT /user-preferences`. */
export const useTheme = () => {
  const { isDarkMode, setDarkMode, logos, colors, companyName } = useThemeStore();
  const updateDarkMode = usePreferencesStore((state) => state.updateDarkMode);

  const { mutate: saveThemePreference } = useMutation({
    mutationFn: (isDark: boolean) => updateUserPreferences({ isDarkMode: isDark }),
    onError: (error, isDark) => {
      setDarkMode(!isDark);
      updateDarkMode(!isDark);
      toast.error(getErrorMessage(error));
    },
  });

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setDarkMode(newMode);
    updateDarkMode(newMode);
    saveThemePreference(newMode);
  };

  const setTheme = (isDark: boolean) => {
    setDarkMode(isDark);
    updateDarkMode(isDark);
    saveThemePreference(isDark);
  };

  return {
    isDarkMode,
    logos,
    colors,
    companyName,
    logoAlt: companyName ? `Logo ${companyName}` : 'Logo',
    toggleTheme,
    setDarkMode: setTheme,
    isTogglingTheme: false,
  };
};
